import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useQuery } from 'data-fetching-client';
import Raven from 'raven-js';
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import { H2, H3 } from 'UIComponents/elements/headings';
import HR from 'UIComponents/elements/HR';
import Small from 'UIComponents/elements/Small';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIBanner from 'UIComponents/alert/UIBanner';
import UIButton from 'UIComponents/button/UIButton';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIImage from 'UIComponents/image/UIImage';
import UIIntersectionObserver from 'UIComponents/observer/UIIntersectionObserver';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UIListingPage from 'UIComponents/page/UIListingPage';
import UISection from 'UIComponents/section/UISection';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UITruncateString from 'UIComponents/text/UITruncateString';
import * as PlanType from 'marketplace-ui-common/constants/pricings/PlanType';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { getCmsFreeSignupUrl } from 'marketplace-ui-assets-core/utils/urls';
import { getInstallsBucket } from 'marketplace-ui-common/utils/installs';
import { isSucceeded, isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { MAIN_SECTION_PADDING_Y } from 'marketplace-ui-common/layout/constants';
import { OfferingReviewType } from 'marketplace-ui-reviews/constants';
import { ResponsiveInnerSection } from 'marketplace-ui-common/layout/components/InnerSection';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import AssetContactInfo from 'marketplace-ui-assets-core/details/components/AssetContactInfo';
import AssetSupport from 'marketplace-ui-assets-core/details/components/AssetSupport';
import AssetProviderProfile from 'marketplace-ui-assets-core/details/components/AssetProviderProfile';
import AssetResources from 'marketplace-ui-assets-core/details/components/AssetResources';
import BigText from 'marketplace-ui-common/components/elements/BigText';
import FeatureCompatibilityModal from 'marketplace-ui-common/compatibility/components/FeatureCompatibilityModal';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import PlanCompatibilityMessage from 'marketplace-ui-common/compatibility/components/PlanCompatibilityMessage';
import * as AppContext from '../../constants/AppContext';
import * as PortalScope from '../../constants/PortalScope';
import { getAssetEventSelectorId, getModuleEventSelectorId, getThemeEventSelectorId } from '../../utils/hsAnalytics'; // @ts-expect-error untyped dependency

import { getHasZorseWebticketScope } from '../../selectors/auth';
import { getAppContext, PublicOnly } from '../../selectors/app'; // @ts-expect-error untyped dependency

import { getPortalScopes } from '../../actions/AuthActions';
import { GET_PROVIDER_CERTIFICATIONS } from '../queries/providerCertificationsQuery'; // @ts-expect-error untyped dependency

import { openHelpWidget } from '../../utils/zorse'; // @ts-expect-error untyped dependency

import { useEmbedContext } from '../../hooks/useEmbedContext';
import { useMissingPortalScopes } from '../../hooks/useMissingPortalScopes';
import AssetDemoButton from './AssetDemoButton';
import AssetDetailsDocumentHead from './documentHead/AssetDetailsDocumentHead';
import AssetDetailsHeader from './AssetDetailsHeader';
import AssetDownloadButton from './AssetDownloadButton';
import AssetFeatures from './AssetFeatures';
import AssetScreenshots from './AssetScreenshots';
import AssetSetupGuideButton from './AssetSetupGuideButton';
import AssetShareButton from './AssetShareButton';
import AssetVersionHistoryModalPanel from '../../details/components/AssetVersionHistoryModalPanel';
import CurrencyText from '../../details/components/CurrencyText';
import HubsParamsTracker from '../../components/HubsParamsTracker';
import MissingScopesMessage from '../../details/components/MissingScopesMessage';
import Reviews from '../../components/reviews/Reviews';
import RouterFilterTagButton from '../../common/components/RouterFilterTagButton';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
const PORTAL_SCOPES = Object.values(PortalScope);
const MISSING_SCOPES_LINK = 'https://knowledge.hubspot.com/resources/how-to-purchase-a-template';
const CERTIFICATIONS_LEARN_MORE_LINK = 'https://academy.hubspot.com/certification-overview';

const NoopExternalIcon = () => null;

const ExternalLink = props => /*#__PURE__*/_jsx(UILink, Object.assign({}, props, {
  ExternalIcon: NoopExternalIcon,
  external: true
}));

const Section = ({
  children,
  label
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});

export default function AssetDetailsPage({
  assetContentsRequestState,
  assetDetails,
  assetScopes,
  offeringType,
  renderedAssetContents,
  renderedAssetRecommendationsSection,
  renderedClientExamples,
  renderedExpandableTagsSection,
  use
}) {
  const {
    trackDetailsInteraction,
    trackDetailsPageview
  } = useContext(StorefrontUsageTrackerContext);
  const {
    isEmbedded
  } = useEmbedContext();
  const appContext = useSelector(getAppContext);
  const isAdminPreview = appContext === AppContext.ADMIN_PREVIEW;
  const isInApp = appContext === AppContext.IN_APP;
  const isPreview = use === 'preview' || isAdminPreview;
  const lang = getLang();
  const {
    data,
    previousData
  } = useQuery(GET_PROVIDER_CERTIFICATIONS);
  const providerCertifications = useMemo(() => {
    const providerCertificationsData = data || previousData;

    if (!providerCertificationsData || isAdminPreview) {
      return {};
    }

    const mappedCerts = {};
    providerCertificationsData.certifications.forEach(cert => {
      mappedCerts[cert.awardName] = cert;
    });
    return mappedCerts;
  }, [data, isAdminPreview, previousData]);
  const hasZorseWebticketScope = useSelector(getHasZorseWebticketScope);
  const dispatch = useDispatch();
  const [reviewsFulfilled, setReviewsFulfilled] = useState(false);
  const {
    cmsBannerButtonWidth,
    isMobileView,
    leftColumnClassName,
    rightColumnInnerClassName
  } = useResponsiveProps({
    cmsBannerButtonWidth: {
      sm: '100px',
      md: '125px',
      lg: '85px'
    },
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    },
    leftColumnClassName: {
      md: 'p-right-10',
      lg: 'p-right-15'
    },
    rightColumnInnerClassName: {
      lg: 'p-x-4',
      xl: 'p-x-8'
    }
  });
  const [featureCompatibilityModalOpen, setFeatureCompatibilityModalOpen] = useState(false);
  const invalidPortalScopes = useMemo(() => {
    return assetScopes.filter(scope => !PORTAL_SCOPES.includes(scope));
  }, [assetScopes]);
  const [invalidPortalScopesRequestState, setInvalidPortalScopesRequestState] = useState('UNINITIALIZED');
  useEffect(() => {
    if (isSucceeded(assetContentsRequestState) && invalidPortalScopes.length && isUninitialized(invalidPortalScopesRequestState) && isInApp) {
      setInvalidPortalScopesRequestState('PENDING');
      dispatch(getPortalScopes(invalidPortalScopes)).then(() => {
        setInvalidPortalScopesRequestState('SUCCEEDED');
      }).catch(() => {
        setInvalidPortalScopesRequestState('FAILED');
      });
    }
  }, [assetContentsRequestState, dispatch, invalidPortalScopes, invalidPortalScopesRequestState, isInApp]);
  useEffect(() => {
    if (isSucceeded(assetContentsRequestState) && invalidPortalScopes.length) {
      invalidPortalScopes.forEach(scope => {
        Raven.captureMessage(`Invalid asset scope - ${scope}`, {
          level: 'warning',
          extra: {
            scope
          }
        });
      });
    }
  }, [invalidPortalScopes, assetContentsRequestState]);
  const {
    benefits,
    casesUrl,
    chatUrl,
    exampleUrl,
    facebookUrl,
    features,
    forumUrl,
    name,
    offering: {
      installsCount,
      firstPublishedAt,
      slug
    },
    offeringId,
    phone,
    pricing: {
      cents,
      planType
    },
    privacy2Url,
    privacy3Url,
    privacyUrl,
    provider,
    reviewSummary,
    screenshots,
    setupUrl,
    support,
    supportEmail,
    supportLanguages,
    supportUrl,
    termsUrl,
    tiers,
    thumbnailUrl,
    versionNumber,
    versionPublishedAt,
    videoUrl
  } = assetDetails;
  const missingPortalScopes = useMissingPortalScopes(assetScopes);
  const signupUrl = getCmsFreeSignupUrl(slug);
  const isFree = planType === PlanType.FREE;
  const installsBucket = getInstallsBucket(installsCount);
  const {
    name: providerName
  } = provider;
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const compatibilityType = useMemo(() => {
    if (missingPortalScopes.length) {
      if (isFree) {
        return 'COMPATIBLE_SOME';
      }

      return 'COMPATIBLE_NONE';
    } else {
      return 'COMPATIBLE_ALL';
    }
  }, [isFree, missingPortalScopes]);
  const renderSupportLanguageTag = useCallback(langCode => /*#__PURE__*/_jsx(RouterFilterTagButton, {
    disabled: isPreview,
    onClick: () => trackDetailsInteraction('click filter tag', {
      filter: 'languages',
      value: langCode
    }),
    queryField: "languages",
    value: langCode,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `SharedI18nStrings.languageNames.${langCode}`
    })
  }, `supported-language-${langCode}`), [isPreview, trackDetailsInteraction]);
  const renderedCertifications = useMemo(() => {
    if (assetDetails.provider.certifications.length) {
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UIFlex, {
          align: "start",
          "aria-label": unescapedText(`marketplace-assets-ui.details.certificationsAriaLabel`) // @ts-expect-error props don't include as
          ,
          as: "ul",
          className: "m-top-2 m-bottom-0 p-left-0",
          direction: "row",
          justify: "start",
          style: {
            listStyle: 'none'
          },
          wrap: "wrap",
          children: assetDetails.provider.certifications.map(certification => {
            const certificationData = providerCertifications[certification];

            if (certificationData) {
              const certificationDisplayName = certificationData.displayNames[lang] || certificationData.defaultName;
              return /*#__PURE__*/_jsx("li", {
                children: /*#__PURE__*/_jsx(UITooltip, {
                  title: certificationDisplayName,
                  children: /*#__PURE__*/_jsx(UIImage, {
                    alt: certificationDisplayName,
                    className: "m-right-1 m-bottom-1",
                    height: 28,
                    width: 28,
                    src: certificationData.iconUrl
                  })
                }, certification)
              });
            }

            return null;
          })
        }), /*#__PURE__*/_jsx(UILink, {
          "aria-label": unescapedText('marketplace-assets-ui.details.providerInfo.ariaLabel'),
          external: true,
          href: CERTIFICATIONS_LEARN_MORE_LINK,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.details.providerInfo.learnMore"
          })
        })]
      });
    }

    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.details.providerInfo.noCertifications"
    });
  }, [assetDetails, providerCertifications, lang]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "ASSET_DETAILS_LOAD",
    children: [/*#__PURE__*/_jsx(AssetDetailsDocumentHead, {
      assetDetails: assetDetails,
      offeringType: offeringType,
      ready: reviewsFulfilled
    }), /*#__PURE__*/_jsxs(UIListingPage, {
      contentAreaFlush: true,
      headerComponent: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsx(AssetDetailsHeader, {
          assetDetails: assetDetails,
          assetScopes: assetScopes,
          exampleUrl: exampleUrl,
          isEmbedded: isEmbedded,
          isPreview: isPreview,
          name: name,
          offeringType: offeringType,
          onTrackInteraction: trackDetailsInteraction,
          onTrackPageview: trackDetailsPageview,
          providerName: providerName,
          reviewSummary: reviewSummary
        })
      }),
      mainSectionBackgroundColor: OLAF,
      pageBackgroundColor: GYPSUM,
      pageLayout: "full-width",
      children: [/*#__PURE__*/_jsx(MainSection, {
        responsive: true,
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(UIGrid, {
            children: [/*#__PURE__*/_jsxs(UIGridItem, {
              className: leftColumnClassName,
              size: {
                xs: 12,
                md: 8
              },
              children: [isInApp && missingPortalScopes.length && (!invalidPortalScopes.length || isSucceeded(invalidPortalScopesRequestState)) ? /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                children: /*#__PURE__*/_jsx(UIAlert, {
                  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "marketplace-assets-ui.details.missingScopesAlert.title",
                    options: {
                      offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}_lowercase`)
                    }
                  }),
                  type: "warning",
                  children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                    message: "marketplace-assets-ui.details.missingScopesAlert.message_jsx",
                    elements: {
                      Link: ExternalLink
                    },
                    options: {
                      href: MISSING_SCOPES_LINK
                    }
                  })
                })
              }) : null, /*#__PURE__*/_jsx(PublicOnly, {
                children: /*#__PURE__*/_jsx(UIBanner, {
                  children: /*#__PURE__*/_jsxs(UIFlex, {
                    align: "center",
                    direction: isMobileView ? 'column' : 'row',
                    justify: "between",
                    wrap: "nowrap",
                    children: [/*#__PURE__*/_jsx("b", {
                      children: isFree ? /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.signUpBanner.freeTitle",
                        options: {
                          offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}`)
                        }
                      }) : /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.signUpBanner.paidTitle",
                        options: {
                          offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}`)
                        }
                      })
                    }), /*#__PURE__*/_jsx(UIButton, {
                      className: isMobileView ? '' : 'm-left-8',
                      "data-test-id": "asset-details-signup-banner-button",
                      href: signupUrl,
                      onClick: () => trackDetailsInteraction('click cms sign up link'),
                      size: "extra-small",
                      style: {
                        display: 'block',
                        width: cmsBannerButtonWidth
                      },
                      use: "primary-white",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.signUpBanner.signUp"
                      })
                    })]
                  })
                })
              }), /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                children: /*#__PURE__*/_jsx(AssetScreenshots, {
                  assetDetails: assetDetails,
                  assetScopes: assetScopes,
                  name: name,
                  offeringType: offeringType,
                  onTrackInteraction: trackDetailsInteraction,
                  onTrackPageview: trackDetailsPageview,
                  providerName: providerName,
                  screenshots: screenshots,
                  videoUrl: videoUrl
                })
              }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                id: "overview",
                children: [/*#__PURE__*/_jsx(H3, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "marketplace-assets-ui.details.overview.title"
                  })
                }), /*#__PURE__*/_jsx(UIExpandableText, {
                  buttonAlign: "left",
                  maxHeight: 400,
                  children: /*#__PURE__*/_jsx(UIBreakString, {
                    children: /*#__PURE__*/_jsx(BigText, {
                      children: /*#__PURE__*/_jsx(SanitizedText, {
                        body: benefits
                      })
                    })
                  })
                })]
              }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                id: "features",
                children: [/*#__PURE__*/_jsx(H3, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "marketplace-assets-ui.details.features.title"
                  })
                }), /*#__PURE__*/_jsx(BigText, {
                  children: /*#__PURE__*/_jsx(AssetFeatures, {
                    features: features,
                    onTrackInteraction: trackDetailsInteraction
                  })
                })]
              }), renderedAssetContents]
            }), /*#__PURE__*/_jsxs(UIGridItem, {
              size: {
                xs: 12,
                md: 4
              },
              children: [/*#__PURE__*/_jsx(MediaQuery, {
                minScreenSize: "md",
                children: /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                  children: /*#__PURE__*/_jsxs(UICardWrapper, {
                    className: "m-bottom-0 p-all-5 p-bottom-6",
                    compact: true,
                    "data-test-id": "asset-actions-card",
                    style: {
                      marginTop: -1 * (124 + MAIN_SECTION_PADDING_Y)
                    },
                    children: [/*#__PURE__*/_jsx(UICardSection, {
                      className: "text-center",
                      children: /*#__PURE__*/_jsx(AbstractTextElement, {
                        className: "is--heading-1",
                        children: isFree ? /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.details.downloadCard.free"
                        }) : /*#__PURE__*/_jsx(CurrencyText, {
                          cents: cents
                        })
                      })
                    }), /*#__PURE__*/_jsx(UICardSection, {
                      children: /*#__PURE__*/_jsxs(UIFlex, {
                        direction: "column",
                        align: "stretch",
                        children: [/*#__PURE__*/_jsx(AssetDownloadButton, {
                          assetDetails: assetDetails,
                          assetScopes: assetScopes,
                          className: "m-bottom-3",
                          "data-test-id": "asset-download-button",
                          offeringType: offeringType,
                          section: "header"
                        }), /*#__PURE__*/_jsx(AssetDemoButton, {
                          className: "m-left-0",
                          exampleUrl: exampleUrl,
                          name: name,
                          onTrackInteraction: trackDetailsInteraction,
                          onTrackPageview: trackDetailsPageview,
                          modalPrimaryButton: /*#__PURE__*/_jsx(AssetDownloadButton, {
                            assetDetails: assetDetails,
                            assetScopes: assetScopes,
                            offeringType: offeringType
                          }),
                          providerName: providerName,
                          section: "header"
                        })]
                      })
                    }), /*#__PURE__*/_jsxs(UICardSection, {
                      className: "text-center",
                      children: [/*#__PURE__*/_jsx(AssetSetupGuideButton, {
                        offeringType: offeringType,
                        setupUrl: setupUrl,
                        style: {
                          width: 'auto'
                        }
                      }), /*#__PURE__*/_jsx(AssetShareButton, {
                        section: "header",
                        style: {
                          width: 'auto'
                        },
                        use: "link"
                      })]
                    })]
                  })
                })
              }), /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                className: rightColumnInnerClassName,
                children: /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.assetInfo.title",
                        options: {
                          offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}`)
                        }
                      })
                    }), /*#__PURE__*/_jsxs(UIDescriptionList, {
                      children: [/*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.details.assetInfo.installs"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: installsBucket > 0 ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
                          message: "marketplace-assets-ui.details.assetInfo.installsBucket.bucket_jsx",
                          elements: {
                            Number: FormattedNumber
                          },
                          options: {
                            value: installsBucket
                          }
                        }) : /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.details.assetInfo.installsBucket.min"
                        })
                      }), /*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.details.assetInfo.created"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: firstPublishedAt != null ? /*#__PURE__*/_jsx(FormattedDateTime, {
                          value: firstPublishedAt,
                          format: "ll"
                        }) : /*#__PURE__*/_jsx("span", {
                          children: "\u2014"
                        })
                      }), /*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.details.assetInfo.version",
                          options: {
                            versionNumber
                          }
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: /*#__PURE__*/_jsxs(UIList, {
                          children: [/*#__PURE__*/_jsx("span", {
                            children: /*#__PURE__*/_jsx(FormattedDateTime, {
                              value: versionPublishedAt,
                              format: "ll"
                            })
                          }), /*#__PURE__*/_jsx("span", {
                            children: /*#__PURE__*/_jsx(UIButton, {
                              use: "link",
                              onClick: () => {
                                setVersionHistoryOpen(true);
                                trackDetailsInteraction('click version history link', {
                                  section: 'sidebar about'
                                });
                              },
                              children: /*#__PURE__*/_jsx(FormattedMessage, {
                                message: "marketplace-assets-ui.details.assetInfo.versionHistory"
                              })
                            })
                          })]
                        })
                      }), renderedExpandableTagsSection]
                    })]
                  }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.requirements.title"
                      })
                    }), /*#__PURE__*/_jsx(UISection, {
                      children: /*#__PURE__*/_jsx(UIDescriptionList, {
                        children: /*#__PURE__*/_jsx(Section, {
                          label: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: 'marketplace-assets-ui.details.requirements.compatibilityHeadingText'
                          }),
                          children: /*#__PURE__*/_jsx(PlanCompatibilityMessage, {
                            appContext: isInApp ? 'app' : 'public',
                            compatibilityType: compatibilityType,
                            helpText: compatibilityType !== 'COMPATIBLE_ALL' ? /*#__PURE__*/_jsx(UIHelpIcon, {
                              title: /*#__PURE__*/_jsx(MissingScopesMessage, {
                                missingPortalScopes: missingPortalScopes,
                                offeringType: offeringType
                              })
                            }) : undefined,
                            onFeaturesLinkClick: () => setFeatureCompatibilityModalOpen(true),
                            onTrackInteraction: trackDetailsInteraction,
                            popoverHeadingText: /*#__PURE__*/_jsx(FormattedMessage, {
                              message: "marketplace-assets-ui.details.requirements.popoverHeading",
                              options: {
                                offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}`)
                              }
                            }),
                            tiers: tiers,
                            tierGroupNames: ['CRM', 'CMS', 'MARKETING', 'SALES', 'SERVICE', 'OPERATIONS']
                          })
                        })
                      })
                    })]
                  }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.providerInfo.title",
                        options: {
                          providerName
                        }
                      })
                    }), /*#__PURE__*/_jsx(UISection, {
                      children: /*#__PURE__*/_jsx(AssetProviderProfile, {
                        onTrackInteraction: (action, eventProperties = {}) => {
                          trackDetailsInteraction(action, Object.assign({}, eventProperties, {
                            section: 'sidebar provider'
                          }));
                        },
                        providerWebsiteAnalyticsId: getAssetEventSelectorId('SIDEBAR-PROVIDER-WEBSITE', offeringType),
                        provider: provider,
                        use: "sidebar"
                      })
                    }), /*#__PURE__*/_jsxs(UIDescriptionList, {
                      children: [/*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.details.providerInfo.certifications"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: renderedCertifications
                      })]
                    })]
                  }), /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                    children: /*#__PURE__*/_jsx(AssetResources, {
                      casesUrl: casesUrl,
                      forumUrl: forumUrl,
                      getAssetEventSelectorId: offeringType === 'THEME' ? getThemeEventSelectorId : getModuleEventSelectorId,
                      offeringType: offeringType,
                      onTrackInteraction: action => trackDetailsInteraction(action, {
                        section: 'sidebar resources'
                      }),
                      setupUrl: setupUrl,
                      supportUrl: supportUrl
                    })
                  }), /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                    children: /*#__PURE__*/_jsx(AssetContactInfo, {
                      chatUrl: chatUrl,
                      facebookUrl: facebookUrl,
                      getAssetEventSelectorId: offeringType === 'THEME' ? getThemeEventSelectorId : getModuleEventSelectorId,
                      onTrackInteraction: action => trackDetailsInteraction(action, {
                        section: 'sidebar contact'
                      }),
                      phone: phone,
                      provider: provider,
                      supportEmail: supportEmail
                    })
                  }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.termsOfService.title"
                      })
                    }), /*#__PURE__*/_jsx("p", {
                      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                        message: "marketplace-assets-ui.details.termsOfService.message_jsx",
                        elements: {
                          Link: ExternalLink
                        },
                        options: {
                          privacyPolicyLink: {
                            href: privacyUrl,
                            onClick: () => trackDetailsInteraction('click privacy policy link', {
                              section: 'sidebar terms'
                            })
                          },
                          termsLink: {
                            href: termsUrl,
                            onClick: () => trackDetailsInteraction('click terms link', {
                              section: 'sidebar terms'
                            })
                          }
                        }
                      })
                    }), privacy2Url || privacy3Url ? /*#__PURE__*/_jsxs(Fragment, {
                      children: [/*#__PURE__*/_jsx(Small, {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.details.termsOfService.otherPrivacyInformation"
                        })
                      }), /*#__PURE__*/_jsx(UIList, {
                        children: [privacy2Url, privacy3Url].map((otherPrivacyUrl, index) => otherPrivacyUrl ? /*#__PURE__*/_jsx(ExternalLink, {
                          href: otherPrivacyUrl,
                          onClick: () => trackDetailsInteraction('click other privacy policy link', {
                            section: 'sidebar terms'
                          }),
                          children: /*#__PURE__*/_jsx(UITruncateString, {
                            children: otherPrivacyUrl
                          })
                        }, `privacy-link-${index}`) : null)
                      })]
                    }) : null]
                  }), hasZorseWebticketScope && /*#__PURE__*/_jsxs(Fragment, {
                    children: [/*#__PURE__*/_jsx(HR, {}), /*#__PURE__*/_jsx("p", {
                      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                        message: "marketplace-assets-ui.details.report.message_jsx",
                        options: {
                          offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}`),
                          onClick: () => {
                            trackDetailsInteraction('click report this asset link', {
                              section: 'sidebar terms'
                            });
                            openHelpWidget({
                              create: true
                            });
                          }
                        },
                        elements: {
                          Link: UILink
                        }
                      })
                    })]
                  })]
                })
              })]
            })]
          })
        })
      }), renderedClientExamples, /*#__PURE__*/_jsx(MainSection, {
        id: "support",
        responsive: true,
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(UIGrid, {
            children: [/*#__PURE__*/_jsxs(UIGridItem, {
              size: {
                xs: 12,
                xl: 8
              },
              children: [/*#__PURE__*/_jsx(H2, {
                className: "is--heading-3",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "marketplace-assets-ui.details.support.title"
                })
              }), /*#__PURE__*/_jsx(AssetSupport, {
                chatUrl: chatUrl,
                facebookUrl: facebookUrl,
                getAssetEventSelectorId: offeringType === 'THEME' ? getThemeEventSelectorId : getModuleEventSelectorId,
                languageTagRenderer: renderSupportLanguageTag,
                onTrackInteraction: trackDetailsInteraction,
                phone: phone,
                provider: provider,
                responsive: true,
                support: support,
                supportEmail: supportEmail,
                supportLanguages: supportLanguages,
                supportUrl: supportUrl
              })]
            }), /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 12,
                xl: 4
              }
            })]
          })
        })
      }), !isAdminPreview && /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(MainSection, {
          id: "reviews",
          responsive: true,
          children: /*#__PURE__*/_jsx(MainSectionInner, {
            children: /*#__PURE__*/_jsx(Reviews, {
              name: name,
              exampleUrl: exampleUrl,
              isPreview: isPreview,
              offeringId: offeringId,
              offeringType: offeringType,
              onTrackInteraction: trackDetailsInteraction,
              providerName: providerName,
              onReviewsFulfilled: setReviewsFulfilled,
              reviewType: OfferingReviewType[offeringType],
              supportUrl: supportUrl,
              thumbnailUrl: thumbnailUrl,
              versionNumber: versionNumber
            })
          })
        }), /*#__PURE__*/_jsx(UIIntersectionObserver, {
          onIntersectionChange: ({
            entries,
            observer
          }) => {
            if (entries[0].isIntersecting) {
              observer.disconnect();
            }
          },
          renderContent: isIntersecting => {
            if (isIntersecting && !isEmbedded) {
              return renderedAssetRecommendationsSection;
            }

            return /*#__PURE__*/_jsx("div", {
              "data-test-id": "load-recommendations"
            });
          }
        })]
      })]
    }), featureCompatibilityModalOpen && /*#__PURE__*/_jsx(FeatureCompatibilityModal, {
      appContext: isAdminPreview ? 'admin-preview' : isInApp ? 'app' : 'public',
      onClose: () => setFeatureCompatibilityModalOpen(false),
      scopeFeatures: assetScopes.reduce((acc, scope) => {
        const scopeFeatureI18nKey = `marketplace-assets-ui.scopeFeatures.${scope}-title`;

        if (isValidI18nKey(scopeFeatureI18nKey)) {
          const feature = {
            text: unescapedText(scopeFeatureI18nKey),
            scopes: [scope]
          };
          return acc.concat(feature);
        }

        return acc;
      }, []),
      offeringType: offeringType,
      trackInteraction: trackDetailsInteraction
    }), versionHistoryOpen && /*#__PURE__*/_jsx(AssetVersionHistoryModalPanel, {
      offeringId: offeringId,
      onClose: () => setVersionHistoryOpen(false),
      onTrackInteraction: trackDetailsInteraction,
      onTrackPageview: trackDetailsPageview
    }), /*#__PURE__*/_jsx(HubsParamsTracker, {}, `asset-details-${slug}`)]
  });
}